import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import useAppDispatch from 'hooks/common/useAppDispatch';
import terms, { isRussianLocale, locale } from 'i18n';
import { SelectOption } from 'models/common';
import { createToast, hideModal, showModal } from 'redux/actions';

import { EntranceTest } from '../../../models/applications/entrance-tests';
import { entranceTestsApi } from '../../../redux/api/applications/entrance-tests-api';

const ONE_HOUR_MS = 60 * 60 * 1000;

const useEntranceExamsRowForm = (
  test: EntranceTest,
  applicationId: number,
  onUpdate
) => {
  const [canPassExam, setCanPassExam] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const dispatch = useAppDispatch();

  const [setTime] = entranceTestsApi.useSetTimeToEntranceTestMutation();
  const [deleteTime] = entranceTestsApi.useDeleteTimeOfEntranceTestMutation();
  const [getTestDates, { data: availableDates }] =
    entranceTestsApi.useLazyGetEntranceTestDatesQuery();
  const [setPassedByCertificate, { isLoading: isUpdating }] =
    entranceTestsApi.useSetPassedBySertificateMutation();

  const { register, control, setValue, handleSubmit, watch } = useForm<{
    dateTime: SelectOption;
  }>({
    mode: 'all',
  });

  const onDateTimeClear = event => {
    event.preventDefault();
    dispatch(
      showModal({
        name: 'CLEAR_EXAM_DATETIME_MODAL',
        data: {
          onApprove: () => {
            deleteTime({ testId: test.id })
              .unwrap()
              .then(() => {
                setValue('dateTime', {});
              })
              .catch(() =>
                dispatch(createToast(terms.EXAM_DATE_DELETE_ERROR, 'danger'))
              )
              .finally(() => {
                dispatch(hideModal());
              });
          },
          testTitle: isRussianLocale()
            ? test.subject.title
            : test.subject.titleForeign,
          testDateTime: watch('dateTime').label,
          testScore: test.score,
        },
      })
    );
  };

  const onCertificateChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStatus = e.target.checked;
    const testId = test.id;

    dispatch(
      showModal({
        name: 'CONFIRM_PASS_BY_CERTIFICATE_MODAL',
        data: {
          testName: test.subject.title,
          newStatus: newStatus,
          testDateTime: watch('dateTime')?.label ?? '',
          onConfirm: async () => {
            try {
              await setPassedByCertificate({
                applicationId,
                testId: testId,
                status: newStatus,
              }).unwrap();
              if (watch('dateTime') && newStatus) {
                await deleteTime({ testId }).unwrap();
                setValue('dateTime', {});
              }
              if (onUpdate) onUpdate();
            } catch (error) {
              console.error(error);
            }
          },
          onCancel: () => {
            e.target.checked = !newStatus;
          },
        },
      })
    );
  };

  useEffect(() => {
    console.log('Даты', test.subject.id, 'applicationId:', applicationId);
    getTestDates({
      testId: test.subject.id,
      applicationId: Number(applicationId),
    });
  }, []);

  useEffect(() => {
    console.log('availableDates:', availableDates);
    console.log('test.scheduleId:', test?.scheduleId);
    console.log('test.datetime:', test?.datetime);
    if (availableDates && test?.scheduleId && test?.datetime) {
      const option = availableDates.find(date => date.id === test.scheduleId);
      console.log('option:', option);
      if (option) {
        const start = new Date(option.testStartDateTime).getTime();
        const end = new Date(option.testEndDateTime).getTime();

        const isTimeActive =
          currentTime.getTime() >= start - ONE_HOUR_MS &&
          currentTime.getTime() < end;

        console.log('start:', new Date(start), 'end:', new Date(end));
        console.log('currentTime:', new Date(currentTime));
        console.log('isTimeActive:', isTimeActive);
        console.log('test.moodleLink:', !!test?.moodleLink);
        console.log('test.testEnded:', test?.testEnded);

        // setCanPassExam(!!test?.moodleLink && !test.testEnded && isTimeActive);
        setCanPassExam(!!test?.moodleLink && !test.testEnded);
        console.log(
          'canPassExam (без времени):',
          !!test?.moodleLink && !test.testEnded
        );

        const formattedLabel = Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(`${option.testDate} ${option.testStart}`));
        setValue('dateTime', {
          label: formattedLabel,
          value: option.id,
        });
      }
    }
  }, [availableDates, test, currentTime, setValue]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fields = {
    dateTime: register('dateTime', {
      onChange: ({ target: { value } }) => {
        setValue('dateTime', value);
        onSubmit();
      },
    }),
  };

  const onSubmit = handleSubmit(data => {
    setTime({
      testId: test.id,
      scheduleId: data.dateTime.value,
    })
      .unwrap()
      .then(() =>
        dispatch(
          createToast(terms.ENTRANCE_EXAM_DATE_CHANGE_SUCCESS, 'success', null)
        )
      )
      .catch(() =>
        dispatch(createToast(terms.EXAM_DATE_CHANGE_ERROR, 'danger'))
      );
  });

  return {
    fields,
    control,
    availableDatesSelectOptions: (availableDates ?? [])
      .filter(date => date.allowed)
      .map(date => ({
        label: Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(`${date.testDate} ${date.testStart}`)),
        value: date.id,
      })),
    hasExamDate: watch('dateTime'),
    canPassExam: watch('dateTime') && canPassExam,
    handleDateTimeClear: onDateTimeClear,
    handleCertificateChange: onCertificateChange,
  };
};

export default useEntranceExamsRowForm;
